import { FC, useEffect, useMemo, useState } from 'react';
import {
  SpheraConfirmation,
  SpheraDropdownEx,
} from '@sphera-cloud/component-ui-reactjs';
import { Grid, GridSortChangeEvent, ISortDescriptor, ISpheraGridMetaData } from 'lca-common-ui-components';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { TruncateText } from 'src/components/TruncateText';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { Job, JobStatus } from 'src/store/slices/jobs/types';
import { deleteJob, fetchJobs, jobsSelector } from 'src/store/slices/jobs';
import { fetchJobSpec, jobSpecSelector } from 'src/store/slices/jobSpec';
import { fetchFileSpec } from 'src/store/slices/fileSpec';
import { Link, useNavigate } from 'react-router-dom';
import { JOBS_RELOAD_TIME } from 'src/utilities/constants';
import { JobSpec } from 'src/store/slices/jobSpec/types';
import { useUserSettings } from 'src/hooks/useUserSettings';
import { ITEMS_PER_PAGE, PAGE_OPTIONS } from 'src/helpers/Constants';

const initialPageOptions = {
  skip: 0,
  take: ITEMS_PER_PAGE,
};

const initialSort: Array<ISortDescriptor> = [{ field: '', dir: 'asc' }];

const JobsList: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>();
  const [typeFilter, setTypeFilter] = useState('');
  const [pageOptions, setPageOptions] = useState<{ skip: number; take: number }>(
    initialPageOptions,
  );
  const [sort, setSort] = useState(initialSort);
  const [selectedId, setSelectedId] = useState<string>('');
  const [confirmation, setConfirmation] = useState<boolean>(false);
  const { getFormattedDateTime } = useUserSettings();

  const { items: jobs, totalCount } = useAppSelector(jobsSelector);
  const initialJobSpec = useAppSelector(jobSpecSelector);
  const jobSpec = initialJobSpec.map((item: JobSpec) => {
    return { label: item.name, value: item.id };
  });
  const typeOptions = [{ label: '', value: '' }, ...jobSpec];

  const changeTypeFilter = (e: any) => {
    setPageOptions((prev) => ({ skip: 0, take: prev.take }));
    setTypeFilter(e?.value.value);
  };

  const typeFilterCell = () => (
    <SpheraDropdownEx
      options={typeOptions}
      type="basic"
      onChange={changeTypeFilter}
      value={typeFilter}
      width={"100%"}
    />
  );

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      dispatch(fetchJobSpec());
      dispatch(fetchFileSpec());
    }, 0);
    return () => clearTimeout(delayDebounceFn);
  }, []);

  const pageChange = (event: { page: { skip: number; take: number } }) => {
    const { skip, take } = event.page;
    setPageOptions({ skip, take });
  };

  const handleSortChange = (e: GridSortChangeEvent) => {
    if (pageOptions.skip !== initialPageOptions.skip) {
      setPageOptions((prev) => ({ skip: 0, take: prev.take }));
    }

    setSort(e.sort as ISortDescriptor[]);
  };

  useEffect(() => {
    let jobsOptions: any = {};
    let delayDebounceFn: ReturnType<typeof setTimeout>;

    const { skip, take } = pageOptions;
    const page = skip / take + 1;
    const status = JobStatus.Running;
    let sortFilter = sort[0]?.field;
    if (sortFilter) {
      sortFilter = `${sortFilter} ${sort[0].dir}`;
    }

    if (filter || typeFilter || sortFilter) {
      // @ts-ignore
      const nameFilter = filter?.filters.find((item) => item.field === 'name')?.value;
      const jobsFilters = { status, page, take, type: typeFilter, name: nameFilter, sort: sortFilter };
      delayDebounceFn = setTimeout(() => {
        dispatch(fetchJobs(jobsFilters));
        jobsOptions = jobsFilters;
      }, 500);
    } else {
      dispatch(fetchJobs({ status, page, take, sort: sortFilter }));
      jobsOptions = { status, page, take, sort: sortFilter };
    }

    const interval = setInterval(() => {
      dispatch(fetchJobs({ ...jobsOptions, isRefresh: true }));
    }, JOBS_RELOAD_TIME);

    return () => {
      clearTimeout(delayDebounceFn);
      clearInterval(interval);
    };
  }, [pageOptions, filter, typeFilter, sort]);

  const openConfirmation = (job: Job) => {
    setConfirmation(true);
    setSelectedId(job.jobId ?? '');
  };

  const closeConfirmation = () => {
    setConfirmation(false);
    setSelectedId('');
  };

  const removeJob = async (id: string) => {
    await dispatch(deleteJob(id));
    closeConfirmation();
    await dispatch(fetchJobs({ status: JobStatus.Running, page: 1, take: 10 }));
  };

  const navigateToJobs = (id: string) => {
    navigate(`/jobs/${id}`);
  };

  const renderName = ({ dataItem }: { dataItem: Job }) => {
    return <td>
      <Link title={dataItem.name ?? ''} to={`/jobs/${dataItem.jobId}`} data-testid="commit-link">
        {dataItem.name}
      </Link>
    </td>;
  };

  const renderDate = ({ dataItem }: { dataItem: Job }) => {
    const date = getFormattedDateTime(dataItem.creationDateTime);
    return <td>
      <TruncateText text={date ?? ''} />
    </td>;
  };

  const renderProgress = ({ dataItem }: { dataItem: Job }) => {
    const progress = `${dataItem.percentComplete}%`;
    return <td>  <TruncateText text={progress ?? ''} /></td>;
  };

  const renderType = ({ dataItem }: { dataItem: Job }) => {
    const itemSpecInfo = initialJobSpec.find(item => dataItem.type === item.id);
    return <td><TruncateText text={itemSpecInfo?.name ?? ''} /></td>;
  };

  const filterChange = (e: any) => {
    setPageOptions((prev) => ({ skip: 0, take: prev.take }));
    setFilter(e?.filter);
  };

  const extendedJobs = useMemo(
    () =>
      jobs.map((job) => ({
        ...job,
        _name: (renderName({ dataItem: job }) as any)?.props?.children,
        _date: (renderDate({ dataItem: job }) as any)?.props?.children,
        _progress: (renderProgress({ dataItem: job }) as any)?.props?.children,
        _type: (renderType({ dataItem: job }) as any)?.props?.children,
      })),
    [jobs, getFormattedDateTime, initialJobSpec],
  );

  const metadata: ISpheraGridMetaData[] = useMemo(
    () => [
      {
        field: 'name',
        title: 'Name',
        filterable: true,
        filterCell: 'text',
        type: 'component',
        object: '_name',
        sortable: true,
      },
      {
        field: 'date',
        title: 'Creation Date',
        width: '260px',
        type: 'component',
        object: '_date',
        filterable: true,
        filterCell: 'custom',
        customFilter: <></>,
        sortable: true,
      },
      {
        field: '',
        title: 'Progress',
        type: 'component',
        object: '_progress',
        width: '110px',
        filterable: true,
        filterCell: 'custom',
        customFilter: <></>,
        sortable: false,
      },
      {
        field: 'type',
        title: 'Type',
        filterable: true,
        filterCell: 'custom',
        customFilter: typeFilterCell(),
        type: 'component',
        object: '_type',
        sortable: true,
      },
    ],
    [typeOptions],
  );

  const actions = useMemo(
    () => [
      {
        text: 'Job Details',
        onClick: (job: Job) => {
          navigateToJobs(job.jobId ?? '');
        },
        icon: 'icon-ui-general-file',
      },
      {
        text: 'Delete Job',
        onClick: (job: Job) => {
          openConfirmation(job);
        },
        icon: 'icon-ui-general-delete',
      },
    ],
    [],
  );

  return (
    <div className="jobs-list--table" id="jobs-list-table">
      <Grid
        data-testid="jobs-list"
        serverSideData
        data={extendedJobs}
        filterable
        filter={filter}
        sort={sort}
        metadata={metadata}
        actions={actions}
        onFilterChange={filterChange}
        onPageChange={pageChange}
        onSortChange={handleSortChange}
        total={totalCount}
        pageOptions={PAGE_OPTIONS}
        {...pageOptions}
      />

      {confirmation && (
        <SpheraConfirmation
          hasAction
          isDangerConfirm
          actionText="Delete"
          onCancel={() => closeConfirmation()}
          onAction={() => removeJob(selectedId)}
          title="Delete this job"
        >
          Once you delete <b>{jobs.find((job) => job.jobId === selectedId)?.name}</b> job, it will be removed forever. Are you sure?
        </SpheraConfirmation>
      )}
    </div>
  );
};

export default JobsList;
